import Image from "next/image"
import { FaRegStar, FaStar } from "react-icons/fa"
import { ReviewMediaCarousel } from "./media-carousel"
import type { ReviewDisplayItem } from "./types"

interface ReviewItemProps {
  review: ReviewDisplayItem
  verifiedLabel?: string
  ratingLabel?: (rating: number) => string
  reviewDateLabel?: (formattedDate: string) => string
}

export function ReviewItem({
  review,
  verifiedLabel = "(comprador verificado)",
  ratingLabel = (rating) => `Avaliacao: ${rating} de 5 estrelas`,
  reviewDateLabel = (formattedDate) => `Data da avaliacao: ${formattedDate}`,
}: ReviewItemProps): React.ReactNode {
  const reviewDate = new Date(review.date)
  const safeReviewDate = Number.isNaN(reviewDate.getTime())
    ? new Date()
    : reviewDate
  const formattedDate = new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "long",
  }).format(safeReviewDate)

  return (
    <div className='border-b py-6'>
      <div className='flex gap-4'>
        <Image
          alt={`Foto de ${review.author}`}
          className='h-10 w-10 rounded-full'
          height={40}
          src={review.avatar}
          width={40}
        />

        <div className='flex-1'>
          <div className='flex items-center gap-2'>
            <span className='font-medium'>{review.author}</span>
            {review.verified && (
              <span className='text-muted-foreground text-xs'>
                {verifiedLabel}
              </span>
            )}
          </div>

          <span className='sr-only'>{ratingLabel(review.rating)}</span>
          <div aria-hidden='true' className='my-1 flex gap-1'>
            {Array.from({ length: 5 }).map((_, index) => (
              <span
                className='text-yellow-500'
                key={`${review.id}-star-${index}`}
              >
                {index < review.rating ? (
                  <FaStar aria-hidden />
                ) : (
                  <FaRegStar aria-hidden />
                )}
              </span>
            ))}
          </div>

          {review.content && (
            <div
              className='text-muted-foreground text-sm'
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Review content is sanitized server-side.
              dangerouslySetInnerHTML={{
                __html: review.content,
              }}
            />
          )}

          {review.media && review.media.length > 0 && (
            <div className='mt-4 max-w-sm'>
              <ReviewMediaCarousel
                media={review.media}
                reviewAuthor={review.author}
              />
            </div>
          )}

          <span className='sr-only'>{reviewDateLabel(formattedDate)}</span>
          <time
            className='mt-2 text-foreground text-xs'
            dateTime={safeReviewDate.toISOString()}
          >
            {formattedDate}
          </time>
        </div>
      </div>
    </div>
  )
}
